import { Box, Button, CardMedia, Grid, Tooltip, Typography, useTheme } from "@mui/material"
import React, { useEffect, useState } from "react"
import { Navigate, useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom"
import { useDirectAPI } from "../services/api/hooks/useDirectAPI"
import { DirectQueryType, DirectType } from "../services/api/direct"
import { useSnackbarContext } from "../services/snackbar"
import { Loading } from "../components/Loading"
import { VideoSuggestions } from "../components/VideoSuggestions"
import { VideoQueryType } from "../services/api/video"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"
import { FormatDate, FormatParagraphs, StylelessLink } from "../components/Util"

import { ReactComponent as PlayIcon } from "../assets/svg/play.svg"
import { ReactComponent as LockIcon } from "../assets/svg/lock.svg"
import { ReactComponent as KeyIcon } from "../assets/svg/key.svg"
import { ReactComponent as InternetIcon } from "../assets/svg/internet.svg"

export const OldURLDirectRedirect = (): React.ReactElement => {
  const { directId: oldFormatDirectId } = useParams<{ directId: string }>()
  return <Navigate to={`/direct?d=${oldFormatDirectId}`} replace={true} />
}

export const Direct = (): React.ReactElement => {
  const [searchParams] = useSearchParams()
  const directId = searchParams.get("d") || "-1"
  const { directsData, loading, error, refresh } = useDirectAPI({
    constraint: DirectQueryType.DIRECT,
    id: directId ?? "-1",
  })
  const {
    directsData: directsDataNext,
    loading: loadingNext,
    refresh: refreshNext,
  } = useDirectAPI({ constraint: DirectQueryType.NEXT, id: directId ?? "-1" })
  const {
    directsData: directsDataPrev,
    loading: loadingPrev,
    refresh: refreshPrev,
  } = useDirectAPI({
    constraint: DirectQueryType.PREVIOUS,
    id: directId ?? "-1",
  })
  const { open: sbOpen } = useSnackbarContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { role, isAuth } = useAuthContext()
  const theme = useTheme()

  const [direct, setDirect] = useState<DirectType | undefined>()
  const [posterSource, setPosterSource] = useState<string | null>(null)
  const [redirectLogin, setRedirectLogin] = useState<boolean>(false)

  useEffect(() => {
    const fetchPoster = async () => {
      try {
        const response = await fetch(`/api/media/poster/${directId}`, {
          credentials: "same-origin",
        })

        if (response.status === 200) {
          const blob = await response.blob()

          const posterURL = URL.createObjectURL(blob)
          setPosterSource(posterURL)
        } else {
          setPosterSource(null)
        }
      } catch (error) {
        console.error("Error fetching poster :", error)
        setPosterSource(null)
      }
    }
    setPosterSource(null)
    fetchPoster()
  }, [directId])

  useEffect(() => {
    refreshNext(true)
    refreshPrev(true)
    refresh()
  }, [directId])

  useEffect(() => {
    if (directsData && directsData[0]) {
      setDirect(directsData[0])
    }
    if (error) {
      if (error.status === 401 || error.status === 403) {
        if (!isAuth()) {
          setRedirectLogin(true)
          sbOpen("Veuillez vous connecter", "info")
          return
        } else {
          sbOpen("Ce direct n'est pas accessible", "error")
        }
      }
      if (error.status === 404) {
        sbOpen("Ce direct n'est pas accessible", "error")
        navigate("/")
        return
      }
      sbOpen("Une erreur s'est produite", "error")
      navigate("/")
    }
  }, [directId, loading, directsData, error])

  if (redirectLogin) {
    return <Navigate to="/login" state={{ from: location }} />
  }

  return !loading && direct ? (
    <Box
      sx={{
        position: theme.name === "glass" ? "static" : "relative",
        overflow: theme.name === "glass" ? "visible" : "hidden",
      }}
    >
      {posterSource && (
        <Box
          sx={{
            position: "absolute",
            top: "0",
            overflow: theme.name === "glass" ? "visible" : "hidden",
            width: "100%",
            minHeight: "100vh",
            maxHeight: "120vh",
          }}
        >
          <CardMedia
            component="img"
            image={posterSource}
            sx={{
              left: "50%",
              width: "100%",
              objectFit: "cover",
              zIndex: 100,
              transform: `translateY(min(${theme.name === "glass" ? -81 : 0}px,calc(300px - 30vw)))`,
              opacity: 0.3,
              filter: "blur(.3vw)",
              maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 20%, rgba(0,0,0,0) min(100%, 120vh))",
            }}
          />
        </Box>
      )}
      <Box sx={{ position: "relative", zIndex: 200 }}>
        <Box sx={{ backgroundColor: "none" }} py={4}>
          <Box display={"flex"} justifyContent={"center"} flexDirection={"row"} alignItems={"start"} px={4}>
            {posterSource ? (
              <Box sx={{ width: "100%", maxWidth: "500px", display: { xs: "none", md: "block" } }}>
                <img src={posterSource} alt={direct.title} width={"100%"} />
              </Box>
            ) : null}
            <Box
              sx={{
                [theme.breakpoints.up("lg")]: { maxWidth: "50%" },
                paddingX: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: posterSource ? "flex-start" : "center",
                textAlign: posterSource ? "left" : "center",
              }}
            >
              <Typography component="h1" variant="h1" fontSize={"3rem"} pt={2}>
                {direct.title}
                <Box ml={1} component={"span"} display="inline">
                  {(role === UserRole.HYRIS || role === UserRole.GEEK) &&
                    (direct.privacy === 0 ? (
                      <Tooltip title={"Direct publique"}>
                        <InternetIcon width={"1.3rem"} height={"1.3rem"} fill={theme.palette.soft.main} />
                      </Tooltip>
                    ) : direct.privacy === 2 ? (
                      <Tooltip title={direct.gpaRestricted ? "Direct caché aux GPA" : "Direct privé"}>
                        <KeyIcon width={"1.3rem"} height={"1.3rem"} fill={theme.palette.soft.main} />
                      </Tooltip>
                    ) : (
                      direct.privacy === 3 && (
                        <Tooltip title={"Direct archivé"}>
                          <LockIcon width={"1.3rem"} height={"1.3rem"} fill={theme.palette.soft.main} />
                        </Tooltip>
                      )
                    ))}
                </Box>
              </Typography>
              {direct.date && (
                <Typography component="h3" variant="h3" fontSize={"1.5rem"} fontWeight={600}>
                  {FormatDate(direct.date)}
                </Typography>
              )}
              {direct.liveUrl && (role === UserRole.HYRIS || role === UserRole.GEEK) && (
                <Box
                  onClick={() => window.location.replace(direct.liveUrl)}
                  width="fit-content"
                  bgcolor="red"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignContent: "center",
                    justifyContent: "flex-start",
                  }}
                  sx={{
                    padding: 1,
                    paddingRight: 2,
                    flexShrink: 0,
                    textAlign: "left",
                    transition: ".3s",
                    cursor: "pointer",
                    borderRadius: "5px",
                    mt: 4,
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "light" ? "#ff3333" : "#c40000",
                    },
                  }}
                >
                  <PlayIcon height="30px" width="30px" style={{ fill: "white", marginRight: "5px" }} />
                  <Typography fontWeight="bold" lineHeight={1.8} color="white">
                    {"REDIFF"}
                  </Typography>
                </Box>
              )}
              <Typography mt={4}>
                <FormatParagraphs content={direct.description} />
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ minHeight: "300px" }} px={"10%"}>
          {role === UserRole.GEEK && (
            <Button
              sx={{ margin: 1 }}
              variant="outlined"
              color="secondary"
              onClick={() => navigate(`/editDirect/${direct.id}`)}
            >
              {"Modifier"}
            </Button>
          )}
          <Grid container>
            <Grid size={6} padding={2}>
              {!loadingPrev && directsDataPrev.length ? (
                <Typography align="left">
                  <StylelessLink to={`/direct?d=${directsDataPrev[0]?.id}`}>
                    {`← ${directsDataPrev[0].title}`}
                  </StylelessLink>
                </Typography>
              ) : (
                <></>
              )}
            </Grid>
            <Grid size={6} padding={2}>
              {!loadingNext && directsDataNext.length ? (
                <Typography align="right">
                  <StylelessLink to={`/direct?d=${directsDataNext[0]?.id}`}>
                    {`${directsDataNext[0].title} →`}
                  </StylelessLink>
                </Typography>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
          <VideoSuggestions query={{ constraint: VideoQueryType.DIRECT, id: direct.id }} md={3} />
        </Box>
      </Box>
    </Box>
  ) : (
    <Loading />
  )
}
